import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["ConfirmButton", "confirmButtonProps", "confirmLabel", "confirmUse", "defaultAction", "description", "disableConfirm", "match", "matcherLabel", "message", "onConfirm", "onReject", "RejectButton", "rejectButtonProps", "rejectLabel", "rejectUse", "role", "use"];
import unescapedText from 'I18n/utils/unescapedText';
import { DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE } from '../../constants';
import { useState } from 'react';
import { DialogHeader } from '../DialogHeader';
import { Text } from '../../../components/Text';
import { ModalDialog } from '../ModalDialog/ModalDialog';
import { Form } from '../../components/Form';
import { DialogBody } from '../DialogBody';
import { Button } from '../Button/Button';
import { FormControl } from '../FormControl/FormControl';
import { MatchTextArea } from '../MatchTextArea/MatchTextArea';
import { DialogFooter } from '../DialogFooter';
import { Paragraph } from '../Paragraph';
import { lazyEval } from '../../utils/lazyEval';
import styled from 'foundations-theming/utils';
import { ConfirmDialogContextProvider } from './ConfirmDialogContext';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CONFIRM = 'confirm';
const REJECT = 'reject';
const StyledModalMatcherOuter = styled.div.withConfig({
  displayName: "ConfirmDialog__StyledModalMatcherOuter"
})(({
  theme
}) => {
  return {
    marginBlockStart: theme.space['300'],
    width: '100%',
    '&:empty': {
      display: 'none'
    }
  };
});
const canSubmit = (confirmButtonProps, disableConfirm, match, isMatched) => {
  if (confirmButtonProps && confirmButtonProps.disabled) return false;
  if (disableConfirm) return false;
  if (match && !isMatched) return false;
  return true;
};
export const ConfirmDialog = _ref => {
  let {
      ConfirmButton = Button,
      confirmButtonProps,
      confirmLabel = unescapedText('salesUI.UIConfirm.defaultConfirm'),
      confirmUse = 'primary',
      defaultAction = REJECT,
      description,
      disableConfirm,
      match,
      matcherLabel,
      message,
      onConfirm,
      onReject,
      RejectButton = Button,
      rejectButtonProps,
      rejectLabel = unescapedText('salesUI.UIConfirm.defaultReject'),
      rejectUse = 'tertiary-light',
      role = 'alertdialog',
      use
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [isMatched, setIsMatched] = useState(false);
  const handleMatchedChange = ({
    target: {
      value
    }
  }) => {
    setIsMatched(value);
  };
  const handleConfirm = () => {
    if (onConfirm) onConfirm(true);
  };
  const handleConfirmClick = evt => {
    evt.stopPropagation(); // Prevent click event from bubbling to window
    // Submit event triggers `handleConfirm`, we don't need to invoke it manually.
  };
  const handleReject = () => {
    if (onReject) onReject(false);
  };
  const handleRejectClick = evt => {
    evt.stopPropagation();
    handleReject();
  };
  const handleSubmit = evt => {
    evt.preventDefault();
    if (canSubmit(confirmButtonProps, disableConfirm, match, isMatched)) {
      handleConfirm();
    }
  };
  return /*#__PURE__*/_jsx(ConfirmDialogContextProvider, {
    value: {
      isConfirmDialog: true
    },
    [DATA_TRANSITIONAL_COMPONENT_ATTRIBUTE]: 'ConfirmDialog',
    children: /*#__PURE__*/_jsx(ModalDialog, Object.assign({}, rest, {
      role: role,
      use: use,
      onEsc: handleReject,
      children: /*#__PURE__*/_jsxs(Form, {
        onSubmit: handleSubmit,
        children: [message ? /*#__PURE__*/_jsx(DialogHeader, {
          children: /*#__PURE__*/_jsx(Text, {
            as: use === 'default' ? 'h4' : 'h2',
            children: message
          })
        }) : null, /*#__PURE__*/_jsxs(DialogBody, {
          children: [typeof description === 'string' ? /*#__PURE__*/_jsx(Paragraph, {
            children: description
          }) : description, match ? /*#__PURE__*/_jsx(StyledModalMatcherOuter, {
            children: /*#__PURE__*/_jsx(FormControl, {
              label: matcherLabel,
              children: /*#__PURE__*/_jsx(MatchTextArea, {
                match: match,
                onMatchedChange: handleMatchedChange,
                size: "xxl"
              })
            })
          }) : null]
        }), /*#__PURE__*/_jsxs(DialogFooter, {
          children: [/*#__PURE__*/_jsx(ConfirmButton, Object.assign({
            type: "submit",
            autoFocus: defaultAction === CONFIRM && !match,
            "data-confirm-button": "accept",
            disabled: !canSubmit(confirmButtonProps, disableConfirm, match, isMatched),
            use: confirmUse,
            onClick: handleConfirmClick
          }, confirmButtonProps, {
            children: lazyEval(confirmLabel)
          })), /*#__PURE__*/_jsx(RejectButton, Object.assign({
            autoFocus: defaultAction === REJECT && !match,
            "data-confirm-button": "reject",
            onClick: handleRejectClick,
            use: rejectUse
          }, rejectButtonProps, {
            children: lazyEval(rejectLabel)
          }))]
        })]
      })
    }))
  });
};
ConfirmDialog.displayName = 'ConfirmDialog';